<template>
  <MotionBackground />
  <FriedModal
    id="create-entity-modal"
    class="create-entity-modal"
    :class="{ signup: isSignup }"
    :closable="false"
    size="large"
    :show-backdrop="false"
    :modal="false"
  >
    <template #header>
      <div v-if="isSignup">
        <FriedGetAcceptLogo class="logo" />
        <div class="signup-header">
          <FriedH2> {{ $t('signup_modal_create_entity_title') }}</FriedH2>
          <FriedParagraph class="description">
            {{ $t('create_new_entity_description') }}
          </FriedParagraph>
        </div>
      </div>
      <FriedModalHeader
        v-else
        :header="$t('create-company-profile')"
        :description="$t('enter-the-name-of-your-company-or-organization')"
      />
    </template>
    <FriedMessage v-if="error" class="error" :title="error.title" type="error">
      <i18n-t :keypath="error.description">
        <template #contact_support>
          <FriedLink normal href="http://help.getaccept.com/" target="_blank">
            {{ $t('contact-support') }}
          </FriedLink>
        </template>
      </i18n-t>
    </FriedMessage>
    <form
      v-if="!isLoading"
      :class="['create-entity-container', { 'sign-up': isSignup, mobile: isSmallScreenAndDown }]"
      @submit.prevent="handleSubmit"
    >
      <FriedInput
        v-model="entityInformation.name"
        :autofocus="!isSignup"
        class="create-entity-input entity-name"
        data-external="create-entity-company-name-input"
        :label="`${$t('company-name')} ${$t('form_required')}`"
        :error="showNameError"
        required
        @update:model-value="updateNameDebounce"
        @blur="updateNameDebounce(entityInformation.name)"
      />
      <FriedSelect
        :model-value="selectedCountry"
        class="create-entity-input"
        :options="countryItems"
        :label="$t('country')"
        option-label="labelWithFlag"
        searchable
        :empty-state-text="$t('no-items-found')"
        @update:model-value="entityInformation = { ...entityInformation, countryCode: $event.key }"
      />
      <template v-if="isSignup">
        <FriedSelect
          v-model="mqlData.salesTeamSize"
          class="create-entity-input"
          :options="salesTeamSizeOptions"
          :label="$t('sales_team_size')"
        />
        <FriedSelect
          v-model="mqlData.crmType"
          class="create-entity-input"
          :options="crmTypeOptions"
          :label="$t('crm_type')"
        />
        <FriedInput
          v-model.trim="mqlData.awarenessInformation"
          data-external="create-entity-awareness-information-input"
          required
          :label="`${$t('how_did_you_hear_about_us')} ${$t('form_required')}`"
        />
      </template>

      <div>
        <FriedSelect
          :model-value="selectedDatacenter"
          class="data-center"
          :options="datacenterItems"
          :label="$t('datacenter')"
          option-label="name"
          :empty-state-text="$t('no-items-found')"
          @update:model-value="entityInformation = { ...entityInformation, datacenter: $event.id }"
        />
        <FriedHelpMessage class="full-width">
          <i18n-t keypath="signup_read_more_data_center_help_message">
            <template #data_center_info_link>
              <FriedLink
                href="https://help.getaccept.com/en/articles/2393314"
                target="_blank"
                :title="t('opens_in_new_tab')"
              >
                {{ t('data_center_info_link') }}
              </FriedLink>
            </template>
          </i18n-t>
        </FriedHelpMessage>
      </div>
    </form>
    <template #footer>
      <FriedModalButtonsFooter>
        <FriedButton
          type="button"
          button-type="secondary"
          data-external="create-entity-cancel-button"
          :disabled="creating"
          @click="handleCancel"
        >
          {{ $t('cancel') }}
        </FriedButton>
        <FriedButton
          type="submit"
          form="createEntityForm"
          data-external="create-entity-submit-button"
          :disabled="disableSubmit"
          :loading="creating"
          @click="handleSubmit"
        >
          {{ isSignup ? $t('create_entity_signup_submit_button') : $t('create') }}
        </FriedButton>
      </FriedModalButtonsFooter>
    </template>
  </FriedModal>
</template>

<script lang="ts" setup>
import { computed, nextTick, ref, watch } from 'vue';
import { useAsyncState, useDebounceFn } from '@vueuse/core';
import { storeToRefs } from 'pinia';
import { useRouter } from 'vue-router';
import { useBreakpoints } from '@getaccept/fried-tofu';
import { useI18n } from 'vue-i18n';
import type { CountryWithLabelFlag } from '@getaccept/lib-shared/src/countries/types/country-with-label-flag';
import { countries } from '@getaccept/lib-shared/src/countries/constants/countries';
import { datacenters } from '@getaccept/lib-shared/src/datacenter/constants/datacenters';
import bugsnagClient from '@getaccept/lib-shared/src/bugsnag';
import MotionBackground from '@getaccept/lib-shared/src/components/MotionBackground.vue';
import { useEnvironment } from '@getaccept/lib-shared/src/composables/environment.composable';
import type { CreateEntityFormData, MqlData } from '../types/create-entity-form-data';
import type { ErrorMessage } from '../../common/types/error-message';
import * as CreateEntityHelper from '../helpers/create-entity.helper';
import * as TimezoneHelper from '../helpers/timezone.helper';
import CreateEntityService from '../services/create-entity.service';
import { useGeoLocation } from '../../location/composables/geo-location.composable';
import * as DatacenterHelper from '../helpers/datacenter.helper';
import { useLanguageStore } from '../../language/store/language.store';

const props = defineProps<{
  isSignup: boolean;
  userId?: string;
  entityName?: string;
  entityCountryCode?: string;
}>();

const { language } = storeToRefs(useLanguageStore());

const { isLocalHost } = useEnvironment();
const { t } = useI18n();
const salesTeamSizeOptions = ['0', '1-4', '5-14', '15-29', '30-49', '50-99', '100+'];
const crmTypeOptions = [
  'Salesforce',
  'Pipedrive',
  'Hubspot CRM',
  'Super Office',
  'Microsoft dynamics CRM',
  'Lime CRM',
  'Other',
  'None',
];

const {
  countryCode,
  isLoading,
  isReady: locationLoaded,
} = useGeoLocation(computed(() => props.entityCountryCode ?? ''));
const { isSmallScreenAndDown } = useBreakpoints();

const datacenter = computed(() => DatacenterHelper.getDatacenterFromCountry(countryCode.value).id);

const mqlData = ref<MqlData>({ salesTeamSize: '', crmType: '', awarenessInformation: '' });

const entityInformation = ref<CreateEntityFormData>({
  name: '',
  countryCode: '',
  timezone: '',
  datacenter: datacenter.value,
  language: '',
});
const recommendedDatacenter = computed(
  () => DatacenterHelper.getDatacenterFromCountry(entityInformation.value.countryCode).id
);

const datacenterItems = computed(() =>
  datacenters.map(datacenter =>
    recommendedDatacenter.value === datacenter.id
      ? { ...datacenter, name: `${datacenter.name} (${t('recommended')})` }
      : datacenter
  )
);

const selectedDatacenter = computed(() =>
  datacenterItems.value.find(datacenter => datacenter.id === entityInformation.value.datacenter)
);

watch(
  locationLoaded,
  () => {
    if (locationLoaded.value) {
      const initialData = {
        name: props.entityName || '',
        countryCode: countryCode.value,
        timezone: TimezoneHelper.getTimezoneAsString(),
        datacenter: datacenter.value,
        language: language.value,
      };
      entityInformation.value = { ...initialData };
    }
  },
  { once: true }
);
const countryItems = computed(() =>
  countries.map(
    (country): CountryWithLabelFlag => ({
      ...country,
      labelWithFlag: `${country.flag} ${country.label}`,
    })
  )
);

const selectedCountry = computed(() =>
  countryItems.value.find(country => country.key === entityInformation.value.countryCode)
);

const error = ref<ErrorMessage>();

const disableSubmit = computed(() =>
  props.isSignup
    ? !entityInformation.value?.name ||
      !mqlData.value?.salesTeamSize ||
      !mqlData.value?.crmType ||
      !mqlData.value?.awarenessInformation
    : !entityInformation.value?.name
);

const showNameError = ref(false);
const updateNameDebounce = useDebounceFn(async (name: string) => {
  await nextTick();
  showNameError.value = !name.length;
}, 200);

const {
  execute: create,
  isLoading: creating,
  isReady: created,
  state: responseUrl,
} = useAsyncState(
  (entityInformation: CreateEntityFormData) => CreateEntityService.createEntity(entityInformation),
  null,
  {
    immediate: false,
    onError: (e: Error) => {
      error.value = CreateEntityHelper.getErrorMessage(e);
      CreateEntityHelper.trackSignupError(error.value);
      if (bugsnagClient) {
        bugsnagClient.notify(e);
      }
    },
  }
);

const router = useRouter();

watch(
  created,
  async () => {
    if (!created.value || !entityInformation.value || !responseUrl.value) return;
    await CreateEntityHelper.trackSignupSuccess(
      props.isSignup,
      props.userId,
      entityInformation.value,
      mqlData.value
    );
    if (props.isSignup) {
      const url = new URL(responseUrl.value);
      url.searchParams.set('autobrand', '');
      window.location.href = url.toString();
    } else {
      window.location.href = responseUrl.value;
    }
  },
  { immediate: true }
);

const handleSubmit = async () => {
  await create(0, entityInformation.value);
};

const handleCancel = () => {
  if (!props.isSignup) {
    window.location.href = '/dashboard';
    return;
  }
  if (isLocalHost.value) {
    router.push('/login');
    return;
  }
  window.location.href = '/';
};
</script>

<style scoped>
@import '../../common/styles/signup-modal.css';

.error {
  margin-bottom: var(--spacing-100);
}
</style>
